import React from 'react';
import { useIntl } from 'react-intl';
import { TextField } from '@mui/material';

import { useStyles } from '../hooks/useStyles';
import AutocompleteLocations from './components/AutocompleteLocations';
import { Location, useGetLocations } from './hooks/useGetLocations';
import { useGetLocation } from './hooks/useGetLocation';

export enum ViewMode {
  VIEW = 'view',
  EDIT = 'edit',
}

export function LocationsOrder({
  formik,
  disabled,
  setStart,
  setEnd,
  lastLocations,
}: {
  formik: any;
  disabled: boolean;
  setStart: (value: Location) => void;
  setEnd: (value: Location) => void;
  lastLocations?: Location[];
}) {
  const {
    fetchSuggest: startFetch,
    loadingLocations: startLoadingLocations,
    locations: startLocations,
    clearLocations: startClearLocations,
  } = useGetLocations();
  const {
    fetchSuggest: endFetch,
    loadingLocations: endLoadingLocations,
    locations: endLocations,
    clearLocations: endClearLocations,
  } = useGetLocations();

  const { values, handleBlur, errors, setFieldValue } = formik;

  const { fetchGeocode, loadingLocation } = useGetLocation(setFieldValue, setStart, setEnd);

  const intl = useIntl();
  const { classes } = useStyles();

  return (
    <>
      <AutocompleteLocations
        options={startLocations || []}
        loading={startLoadingLocations || loadingLocation}
        inputValue={values.startLocation}
        editable={!disabled}
        label={intl.formatMessage({
          id: 'ORDER.TABLE.START.LOCATION',
        })}
        inputClassName={classes.autoLoc}
        inputError={Boolean(errors.startLocation?.address && errors.startLocation?.address)}
        inputHelperText={errors.startLocation?.address && errors.startLocation?.address}
        fetchLocations={({ location }) => startFetch(location)}
        clearLocations={async () =>
          setFieldValue('startLocation', {
            lat: 0,
            lng: 0,
            address: '',
            province: '',
          })
        }
        setSelectedLocation={location => {
          if ((!location.lat || !location.lng) && location.formatted_address) {
            fetchGeocode({
              value: location.formatted_address,
              startLocation: true,
              address: `${location.description ? `${location.description}; ` : ''}${location.address}`,
            });
          } else {
            setFieldValue('startLocation', location);
            setStart(location);
          }
        }}
        handleBlur={handleBlur}
        disable={disabled}
        lastLocations={lastLocations}
        clearField={startClearLocations}
      />
      <TextField
        type='text'
        label='Регион точки отправления'
        margin='normal'
        value={values.startLocation.province || ''}
        variant='outlined'
        inputProps={{ readOnly: true }}
      />
      <AutocompleteLocations
        options={endLocations || []}
        loading={endLoadingLocations || loadingLocation}
        inputValue={values.endLocation}
        editable={!disabled}
        label={intl.formatMessage({
          id: 'ORDER.TABLE.END.LOCATION',
        })}
        inputClassName={classes.autoLoc}
        inputError={Boolean(errors.endLocation?.address && errors.endLocation?.address)}
        inputHelperText={errors.endLocation?.address && errors.endLocation?.address}
        fetchLocations={({ location }) => endFetch(location)}
        clearLocations={async () =>
          setFieldValue('endLocation', {
            lat: 0,
            lng: 0,
            address: '',
            province: '',
          })
        }
        setSelectedLocation={location => {
          if ((!location.lat || !location.lng) && location.formatted_address) {
            fetchGeocode({
              value: location.formatted_address,
              endLocation: true,
              address: `${location.description ? `${location.description}; ` : ''}${location.address}`,
            });
          } else {
            setFieldValue('endLocation', location);
            setEnd(location);
          }
        }}
        handleBlur={handleBlur}
        disable={disabled}
        lastLocations={lastLocations}
        clearField={endClearLocations}
      />
      <TextField
        type='text'
        label='Регион точки назначения'
        margin='normal'
        value={values.endLocation.province || ''}
        variant='outlined'
        inputProps={{ readOnly: true }}
      />
    </>
  );
}
